import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CircleIcon from "@mui/icons-material/Circle";
import { Base } from "../../../../framework/base";
import { useTranslation } from "react-i18next";
import {
    TransportOrderListItemDto,
    TransportOrderState,
    transportOrderStates,
    transportOrderStateNames,
} from "../../../../models/transport/transportOrder";

interface VehicleOrdersSectionProps {
    orders: TransportOrderListItemDto[];
}

const getStateColor = (state: TransportOrderState): string => {
    return (
        transportOrderStates.find((s) => s.value === state)?.color ?? "#999"
    );
};

const formatDateTime = (dateStr?: string): string => {
    if (!dateStr) return "-";
    return Base.dayjsToMinuteAccuracy(dateStr).format("DD.MM HH:mm");
};

const VehicleOrdersSection = ({ orders }: VehicleOrdersSectionProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(
        null
    );
    const [selectedOrder, setSelectedOrder] =
        useState<TransportOrderListItemDto | null>(null);

    if (!orders || orders.length === 0) return null;

    return (
        <Box sx={{ mt: 0.5 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    py: 0.25,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                }}
            >
                <Box component="span" sx={{ display: "inline-flex", mr: 0.5 }}>
                    {expanded ? (
                        <ExpandLessIcon fontSize="small" />
                    ) : (
                        <ExpandMoreIcon fontSize="small" />
                    )}
                </Box>
                <Typography variant="caption" fontWeight="bold">
                    {t("monitorView.orders.title")} ({orders.length})
                </Typography>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ pl: 1 }}>
                    {orders.map((order) => (
                        <Box
                            key={order.id}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                py: 0.5,
                                cursor: "pointer",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                },
                                borderRadius: 0.5,
                                px: 0.5,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrder(order);
                                setPopoverAnchor(e.currentTarget);
                            }}
                        >
                            <CircleIcon
                                sx={{
                                    fontSize: 10,
                                    color: getStateColor(order.state),
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    noWrap
                                    component="div"
                                >
                                    #{order.orderNumber}{" "}
                                    {order.name && (
                                        <Typography
                                            variant="caption"
                                            component="span"
                                        >
                                            {order.name}
                                        </Typography>
                                    )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="div"
                                    sx={{ fontSize: "0.7rem" }}
                                >
                                    {t(transportOrderStateNames[order.state])}
                                    {" · "}
                                    {formatDateTime(
                                        order.scheduledStartDateTime
                                    )}
                                    {" - "}
                                    {formatDateTime(order.scheduledEndDateTime)}
                                </Typography>
                                {(order.senderName || order.receiverName) && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                        noWrap
                                        sx={{ fontSize: "0.7rem" }}
                                    >
                                        {order.senderName ?? ""}
                                        {order.senderName && order.receiverName && " → "}
                                        {order.receiverName ?? ""}
                                    </Typography>
                                )}
                                {order.driverComments && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                        noWrap
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        {order.driverComments}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Collapse>
            <Popover
                open={!!popoverAnchor}
                anchorEl={popoverAnchor}
                onClose={() => {
                    setPopoverAnchor(null);
                    setSelectedOrder(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                onClick={(e) => e.stopPropagation()}
            >
                {selectedOrder && (
                    <OrderDetailsPopoverContent
                        order={selectedOrder}
                        getStateColor={getStateColor}
                        formatDateTime={formatDateTime}
                    />
                )}
            </Popover>
        </Box>
    );
};

const DetailRow = ({
    label,
    value,
}: {
    label: string;
    value: React.ReactNode;
}) => {
    if (!value) return null;
    return (
        <Box sx={{ display: "flex", gap: 1, py: 0.25 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 80, flexShrink: 0 }}
            >
                {label}
            </Typography>
            <Typography variant="caption">{value}</Typography>
        </Box>
    );
};

const OrderDetailsPopoverContent = ({
    order,
    getStateColor,
    formatDateTime,
}: {
    order: TransportOrderListItemDto;
    getStateColor: (state: TransportOrderState) => string;
    formatDateTime: (dateStr?: string) => string;
}) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ p: 1.5, maxWidth: 320, minWidth: 240 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                }}
            >
                <CircleIcon
                    sx={{
                        fontSize: 10,
                        color: getStateColor(order.state),
                    }}
                />
                <Typography variant="body2" fontWeight="bold">
                    #{order.orderNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {t(transportOrderStateNames[order.state])}
                </Typography>
            </Box>
            {order.name && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {order.name}
                </Typography>
            )}
            <Divider sx={{ my: 0.5 }} />
            <DetailRow
                label={t("monitorView.orders.scheduled")}
                value={`${formatDateTime(order.scheduledStartDateTime)} - ${formatDateTime(order.scheduledEndDateTime)}`}
            />
            <DetailRow
                label={t("monitorView.orders.pickup")}
                value={order.senderName}
            />
            <DetailRow
                label={t("monitorView.orders.delivery")}
                value={order.receiverName}
            />
            <DetailRow
                label={t("monitorView.orders.billingCustomer")}
                value={order.billingCustomerName}
            />
            {order.transportDetails && (
                <DetailRow
                    label={t("monitorView.orders.details")}
                    value={order.transportDetails}
                />
            )}
            {order.products?.length > 0 && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mb: 0.25 }}
                    >
                        {t("monitorView.orders.products")}
                    </Typography>
                    {order.products.map((p) => (
                        <Typography
                            key={p.id}
                            variant="caption"
                            component="div"
                        >
                            {p.quantity} {p.unitName} — {p.name}
                        </Typography>
                    ))}
                </>
            )}
            {order.driverComments && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                    >
                        {t("monitorView.orders.driverComments")}
                    </Typography>
                    <Typography variant="caption" component="div">
                        {order.driverComments}
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default VehicleOrdersSection;
